import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import { createPortal } from 'react-dom';
import MuleBadge from '../../components/MuleBadge';
import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import GridLabel from '../../components/GridLabel';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import api from '../../services/api';
import styles from './SafetyHoursOfService.module.css';
import { formatHosStart, selectHosOutlierExtremes, summarizeHosDrivers, summarizeHosMarkets, type HosDriverSummary } from '../../services/hosReport';

interface DutyDay {
  samsara_driver_id: string;
  driver_name: string | null;
  driver_number: string | null;
  on_duty_span_minutes: number | null;
  isMule?: boolean;
  duty_date: string;
  first_on_duty_at: string | null;
  last_on_duty_at: string | null;
  off_duty_at: string | null;
  first_driving_at: string | null;
  market: string;
  driving_minutes: number | null;
  on_duty_minutes: number | null;
  spans_midnight: boolean;
  off_duty_next_day: boolean;
  carried_over: boolean;
  working_status_changes: number;
  status_changes: number;
}

const DEFAULT_RANGE_DAYS = 30;

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** 2026-08-17T06:00:00 → "6:00 AM". The view already converted to local time,
 *  so these are wall-clock values and must not be re-shifted by the browser. */
function formatTimeOfDay(value: string | null): string {
  if (!value) return '';
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  const [hourStr, minute] = timePart.split(':');
  const hour = parseInt(hourStr, 10);
  if (Number.isNaN(hour)) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return 'Unavailable';
  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const mins = roundedMinutes % 60;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

function TimeDetails({ summary, kind = 'start' }: { summary: ReturnType<typeof summarizeHosMarkets>[number]; kind?: 'start' | 'end' }) {
  const average = kind === 'start' ? summary.averageStartMinutes : summary.averageEndMinutes;
  const deviation = kind === 'start' ? summary.startStddevMinutes : summary.endStddevMinutes;
  const count = kind === 'start' ? summary.startDays : summary.endDays;
  const outliers = kind === 'start' ? summary.startOutliers : summary.endOutliers;
  const displayedOutliers = selectHosOutlierExtremes(outliers);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const cancelClose = () => { if (timer.current) clearTimeout(timer.current); };
  const closeSoon = () => { cancelClose(); timer.current = setTimeout(() => setPosition(null), 180); };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => {
    if (!position) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setPosition(null); };
    const hide = () => setPosition(null);
    document.addEventListener('keydown', close);
    window.addEventListener('resize', hide);
    return () => { document.removeEventListener('keydown', close); window.removeEventListener('resize', hide); };
  }, [position]);
  const open = (element: HTMLElement) => {
    cancelClose();
    const rect = element.getBoundingClientRect();
    setPosition({ top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - Math.min(600, window.innerHeight - 16) - 8)),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 568)) });
  };
  return <>
    <button type="button" className="underline decoration-dotted underline-offset-4 whitespace-nowrap"
      aria-label={`${summary.market} average ${kind} time details`} aria-expanded={!!position} aria-controls={position ? id : undefined}
      onMouseEnter={event => open(event.currentTarget)} onMouseLeave={closeSoon}
      onFocus={event => open(event.currentTarget)} onBlur={closeSoon}
      onClick={event => open(event.currentTarget)}>
      {formatHosStart(average)}
    </button>
    {position && createPortal(<div id={id} role="region" aria-label={`${summary.market} ${kind} time statistics`}
      onMouseEnter={cancelClose} onMouseLeave={closeSoon} onFocus={cancelClose} onBlur={closeSoon}
      tabIndex={0} style={{ ...position, position: 'fixed', zIndex: 9999, width: 560, maxWidth: 'calc(100vw - 16px)', maxHeight: `calc(100dvh - ${position.top + 8}px)`, height: 'max-content', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      className="rounded-lg border border-gray-300 dark:border-gray-600 bg-panel text-body p-3 shadow-xl text-sm font-normal">
      <div className="shrink-0">
      <strong>{summary.market} {kind} times</strong>
      <p>{count} {kind}s · Standard deviation: {deviation === null ? 'Unavailable' : `${deviation.toFixed(1)} minutes`}</p>
      <p className="text-xs my-2">Daily {kind}s more than 2 standard deviations from the average. Times are Central; midnight carryovers {kind === 'end' ? 'and unknown ends ' : ''}excluded. Times across midnight are averaged around their shared clock-time center.</p>
      </div>
      {outliers.length ? <>
      <div className="min-h-0 overflow-auto overscroll-contain" style={{ maxHeight: 420, scrollbarGutter: 'stable' }} tabIndex={0} aria-label={`${kind} time outliers`}>
      <table className="w-full text-left">

        <thead className="sticky top-0 bg-panel"><tr>{['Outlier', 'Driver #', 'Name', 'Date', kind === 'start' ? 'Start time' : 'End time'].map(label => <th key={label} className="p-1">{label}</th>)}</tr></thead>
        <tbody>{displayedOutliers.map((row, index) => <tr key={index}>
          <td className="p-1">{row.direction}</td><td className="p-1">{row.driver_number || 'Unavailable'}</td>
          <td className="p-1">{row.driver_name || 'Unknown'}</td><td className="p-1 whitespace-nowrap">{row.duty_date}</td>
          <td className="p-1 whitespace-nowrap">{formatHosStart(row.minutes)}</td>
        </tr>)}</tbody>
      </table>
      </div>
      <p className="shrink-0 pt-2 text-xs text-muted">{outliers.length} outliers · Showing up to 3 earliest and 3 latest.</p>
      </> : <p>{average === null ? 'No reliable average available.' : 'No early or late outliers.'}</p>}
    </div>, document.body)}
  </>;
}

export default function SafetyHoursOfService() {
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();

  const todayStr = toDateInput(new Date());
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - DEFAULT_RANGE_DAYS);

  const [startDate, setStartDate] = useState(toDateInput(defaultStart));
  const [endDate, setEndDate] = useState(todayStr);
  const [rows, setRows] = useState<DutyDay[]>([]);
  const [reportTab, setReportTab] = useState<'daily' | 'averages'>('daily');
  useFavoriteTab(reportTab, setReportTab, ['daily', 'averages']);
  const [market, setMarket] = useState('');
  const visibleRows = useMemo(() => rows.filter(row => !market || row.market === market), [rows, market]);
  const driverSummaries = useMemo(() => summarizeHosDrivers(visibleRows), [visibleRows]);
  const summaries = useMemo(() => summarizeHosMarkets(visibleRows), [visibleRows]);
  const markets = useMemo(() => [...new Set(rows.map(row => row.market))].sort(), [rows]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/hos-duty-day/report', {
        params: { start_date: startDate, end_date: endDate },
      });
      setRows(response.data.rows);
      setError(null);
    } catch (err: unknown) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching duty-day report:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const columns: ColumnDef<DutyDay>[] = useMemo(() => [
    { accessorKey: 'driver_name', header: 'Driver', size: 200, cell: ({ row }) => <>{row.original.driver_name}<MuleBadge isMule={row.original.isMule} /></>, meta: { exportValue: (row: { original: DutyDay }) => `${row.original.driver_name || ''}${row.original.isMule ? ' (Mule)' : ''}` } },
    { accessorKey: 'market', header: 'Market', size: 140 },
    { accessorKey: 'duty_date', header: 'Date', size: 120 },
    {
      accessorKey: 'first_on_duty_at',
      header: 'First On Duty',
      size: 170,
      cell: ({ row, getValue }) => (
        <span>
          {formatTimeOfDay(getValue() as string | null)}
          {row.original.carried_over && (
            <span
              className="ml-1 text-xs text-muted"
              title="The driver was already in a working status at midnight — this is the day boundary, not a clock-in."
            >
              (carried over)
            </span>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'off_duty_at',
      header: 'Off Duty',
      size: 150,
      cell: ({ row, getValue }) => (
        <span>
          {formatTimeOfDay(getValue() as string | null)}
          {row.original.off_duty_next_day && (
            <span
              className="ml-1 text-xs text-blue-600 dark:text-blue-400"
              title="This shift ended after midnight, so the off-duty time is on the following date."
            >
              +1d
            </span>
          )}
          {row.original.spans_midnight && (
            <span
              className="ml-1 text-xs text-amber-600 dark:text-amber-400"
              title="No off-duty change was found within 24 hours of the last working status, so the end of this shift is unknown."
            >
              unknown
            </span>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'driving_minutes',
      header: 'Total Driving',
      size: 110,
      cell: ({ getValue }) => formatDuration(getValue() as number | null),
    },
    {
      accessorKey: 'on_duty_minutes',
      header: 'Total On Duty',
      size: 140,
      cell: ({ getValue }) => formatDuration(getValue() as number | null),
    },
    {
      accessorKey: 'first_driving_at',
      header: 'First Driving',
      size: 140,
      cell: ({ getValue }) => formatTimeOfDay(getValue() as string | null),
    },
    {
      accessorKey: 'last_on_duty_at',
      header: 'Last On Duty',
      size: 140,
      enableHiding: true,
      meta: { initiallyHidden: true },
      cell: ({ getValue }) => formatTimeOfDay(getValue() as string | null),
    },
    { accessorKey: 'status_changes', header: 'Status Changes', size: 130 },
    {
      accessorKey: 'samsara_driver_id',
      header: 'Samsara Driver ID',
      size: 160,
      enableHiding: true,
      meta: { initiallyHidden: true },
    },
  ], []);

  const driverColumns: ColumnDef<HosDriverSummary>[] = useMemo(() => [
    { accessorKey: 'driver_name', header: 'Driver', size: 200,
      cell: ({ row }) => <>{row.original.driver_name}<MuleBadge isMule={row.original.isMule} /></>,
      meta: { exportValue: (row: { original: HosDriverSummary }) => `${row.original.driver_name}${row.original.isMule ? ' (Mule)' : ''}` } },
    { accessorKey: 'market', header: 'Market', size: 140 },
    { id: 'averageStartMinutes', header: 'Avg Start Time', size: 150,
      accessorFn: row => row.averageStartMinutes ?? undefined, sortingFn: 'basic', sortUndefined: 'last',
      cell: ({ getValue }) => formatHosStart(getValue() as number | undefined),
      meta: { exportValue: (row: { original: HosDriverSummary }) => formatHosStart(row.original.averageStartMinutes) } },
    { id: 'averageEndMinutes', header: 'Avg End Time', size: 150,
      accessorFn: row => row.averageEndMinutes ?? undefined, sortingFn: 'basic', sortUndefined: 'last',
      cell: ({ getValue }) => formatHosStart(getValue() as number | undefined),
      meta: { exportValue: (row: { original: HosDriverSummary }) => formatHosStart(row.original.averageEndMinutes) } },
    { accessorKey: 'workedDays', header: 'Worked Days', size: 120 },
    ...([
      ['averageWorkingMinutes', 'Avg Working Hours'],
      ['averageDrivingMinutes', 'Avg Daily Driving'],
      ['averageOnDutyMinutes', 'Avg Daily On Duty'],
      ['drivingMinutes', 'Total Driving'],
      ['onDutyMinutes', 'Total On Duty'],
    ] as const).map(([accessorKey, header]): ColumnDef<HosDriverSummary> => ({
      id: accessorKey, header, size: 160, sortingFn: 'basic', sortUndefined: 'last',
      accessorFn: row => (accessorKey === 'drivingMinutes' && !row.drivingDays) ||
        (accessorKey === 'onDutyMinutes' && !row.onDutyDays) ? undefined : row[accessorKey] ?? undefined,
      cell: ({ getValue }) => formatDuration(getValue() as number | null),
      meta: { exportValue: (row: { original: HosDriverSummary }) => formatDuration(
        (accessorKey === 'drivingMinutes' && !row.original.drivingDays) ||
        (accessorKey === 'onDutyMinutes' && !row.original.onDutyDays) ? null : row.original[accessorKey]) },
    })),
    ...([
      ['drivingPercent', 'Driving Utilization %'],
      ['onDutyPercent', 'On-Duty Utilization %'],
    ] as const).map(([id, header]): ColumnDef<HosDriverSummary> => ({
      id, accessorFn: row => row[id] ?? undefined, header, size: 180,
      sortingFn: 'basic', sortUndefined: 'last',
      cell: ({ getValue }) => getValue() == null ? 'Unavailable' : `${(getValue() as number).toFixed(1)}%`,
      meta: { exportValue: (row: { original: HosDriverSummary }) => row.original[id] === null ? 'Unavailable' : `${row.original[id].toFixed(1)}%` },
    })),
    { accessorKey: 'drivingDays', header: 'Days With Driving Totals', size: 180 },
    { accessorKey: 'onDutyDays', header: 'Days With On-Duty Totals', size: 190 },
    { accessorKey: 'missingDays', header: 'Unavailable Days', size: 150 },
  ], []);

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      <div className={`${styles.body} px-4 sm:px-6 lg:px-8 py-8`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />

        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-heading">
              Safety - Hours of Service
            </h1>
            <p className="mt-2 text-sm text-label">
              Daily driving and working time by driver and market, from Samsara HOS duty-status logs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            From
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
            />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            To
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayStr}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
            />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-label">
            Market
            <select value={market} onChange={event => setMarket(event.target.value)} className="border rounded px-2 py-1 bg-panel text-body">
              <option value="">All markets</option>
              {markets.map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
        </div>

        {!loading && !error && <section aria-label="Market utilization" className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-panel">
          <h2 className="text-lg font-semibold text-heading">Market utilization</h2>
          <p className="mt-1 mb-3 text-sm text-label">
            Selected dates and market: total hours ÷ (worked driver-days × 11 driving hours or 14 on-duty hours).
            On duty includes driving and yard moves; breaks, sleeper and personal conveyance are excluded.
            Averages are per worked driver-day, rounded to the nearest minute. Days with unavailable totals are excluded from that average and percentage. Start and end averages use actual first-on-duty and off-duty times for shifts starting in the selected dates, excluding midnight carryovers and unknown ends from end averages; hover or focus either average for outliers. Table search and column filters apply to details below.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-body">
              <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                {['Market', 'Average Start Time', 'Average End Time', 'Worked driver-days', 'Total driving', 'Avg driving / driver-day', 'Driving used / 11h', 'Total on duty', 'Avg on duty / driver-day', 'On duty used / 14h', 'Unavailable days'].map(label => <th key={label} className="p-2 font-medium">{label}</th>)}
              </tr></thead>
              <tbody>{summaries.map((summary, index) => <tr key={`${index}-${summary.market}`} className={index === 0 ? 'font-semibold bg-gray-50 dark:bg-gray-800' : ''}>
                <td className="p-2">{summary.market}</td>
                <td className="p-2"><TimeDetails summary={summary} /></td>
                <td className="p-2"><TimeDetails summary={summary} kind="end" /></td>
                <td className="p-2">{summary.workedDays}</td>
                <td className="p-2">{summary.drivingDays ? formatDuration(summary.drivingMinutes) : 'Unavailable'}</td>
                <td className="p-2">{formatDuration(summary.averageDrivingMinutes)}</td>
                <td className="p-2">{summary.drivingPercent === null ? 'Unavailable' : `${summary.drivingPercent.toFixed(1)}%`}</td>
                <td className="p-2">{summary.onDutyDays ? formatDuration(summary.onDutyMinutes) : 'Unavailable'}</td>
                <td className="p-2">{formatDuration(summary.averageOnDutyMinutes)}</td>
                <td className="p-2">{summary.onDutyPercent === null ? 'Unavailable' : `${summary.onDutyPercent.toFixed(1)}%`}</td>
                <td className="p-2">{summary.missingDays}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted">Utilization is an operating benchmark. The legal 14-hour driving window measures elapsed time and differs from summed on-duty time.</p>
        </section>}

        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 mb-4 flex gap-2">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-400" aria-hidden="true" />
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Times are Central. Driving and on-duty totals sum recorded intervals within each calendar day, splitting at midnight. Off duty is the first
            non-working change after that day's last working status, so a night shift correctly ends
            the next morning and is marked <span className="text-blue-600 dark:text-blue-400">+1d</span>.
            A shift whose end could not be found within 24 hours shows{' '}
            <span className="text-amber-600 dark:text-amber-400">unknown</span> rather than a guess.
            <em>(carried over)</em> means the driver was already working at midnight, so the 12:00 AM
            is the day boundary and not a clock-in. On duty covers onDuty, driving and yard move;
            personal conveyance and sleeper do not. Average Working Hours measures the span from first on duty to off duty, including breaks, for starts in the selected dates; carryovers and unknown ends are excluded. The most recent day or two keeps filling in as
            Samsara receives uploads from the driver app.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading hours of service
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div role="tablist" aria-label="HOS report views" className="flex items-end gap-0.5 overflow-x-auto">
          {([
            { id: 'daily', label: 'Daily Totals', count: visibleRows.length },
            { id: 'averages', label: 'Driver Averages', count: driverSummaries.length },
          ] as const).map(tab => <button
            key={tab.id} type="button" role="tab" id={`hos-tab-${tab.id}`}
            aria-selected={reportTab === tab.id} aria-controls={`hos-panel-${tab.id}`}
            tabIndex={reportTab === tab.id ? 0 : -1}
            onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 'daily' : event.key === 'End' ? 'averages' : tab.id === 'daily' ? 'averages' : 'daily';
              setReportTab(next);
              document.getElementById(`hos-tab-${next}`)?.focus();
            }}
            onClick={() => setReportTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium rounded-t border whitespace-nowrap ${reportTab === tab.id
              ? 'bg-panel text-blue-700 dark:text-blue-300 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 -mb-px font-semibold'
              : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >{tab.label} ({tab.count.toLocaleString()})</button>)}
        </div>
        <div role="tabpanel" id={`hos-panel-${reportTab}`} aria-labelledby={`hos-tab-${reportTab}`}>
        {reportTab === 'daily' ? <TanTable
          key="daily"
          data={visibleRows}
          columns={columns}
          settingsKey="safety-hours-of-service-table"
          fieldName="safety-hours-of-service-table"
          searchPlaceholder="Search drivers..."
          cardTitle="Driver Duty Days"
          loading={loading}
          enableColumnFilters={true}
          enableExcelExport={true}
          enableSearch={true}
          enableReset={true}
          enableShowHideCols={true}
          enableRowsPerPage={true}
          enableZebraStriping={true}
          enableGrouping={true}
          errorMessage={error}
          onRetry={fetchReport}
        /> : <TanTable
          key="averages"
          data={driverSummaries}
          columns={driverColumns}
          settingsKey="safety-hours-of-service-driver-averages"
          fieldName="safety-hours-of-service-driver-averages"
          cardTitle="Driver Averages & Utilization"
          searchPlaceholder="Search drivers..."
          defaultSorting={[{ id: 'drivingPercent', desc: true }]}
          loading={loading}
          errorMessage={error}
          onRetry={fetchReport}
          enableColumnFilters
          enableExcelExport
          enableSearch
          enableReset
          enableShowHideCols
          enableRowsPerPage
          enableZebraStriping
          enableGrouping
        />}
        </div>
      </div>
    </div>
  );
}
